// Utility to fetch machine software versions and match with version data

import axios from 'axios';

const CONFIG_API_URL = 'https://78e2yayiy4.execute-api.eu-central-1.amazonaws.com/prod/machine-configs';
const MACHINE_VERSION_API_URL = 'https://t9a6gczs75.execute-api.eu-central-1.amazonaws.com/prod/machine-version';

export interface MachineConfig {
  machine_id: string;
  hw_id: string;
  customer_name: string;
  location: string;
  status: string;
  hw_type: string;
  ip_address: string;
  ip_office: string;
  sim_card: string;
  data_active: string;
  data_active_until: string;
  software_version: string;
  comments: string;
  last_updated: string | null;
}

export interface MachinesByVersion {
  [versionString: string]: string[]; // version -> array of machine IDs
}

export const fetchMachineVersions = async (): Promise<MachinesByVersion> => {
  try {
    const response = await axios.get(CONFIG_API_URL);
    const configs = response.data.configs || {};

    const machinesByVersion: MachinesByVersion = {};

    // Group machines by their software version
    Object.entries(configs).forEach(([machineId, config]: [string, any]) => {
      const version = config.software_version || 'Unknown';

      if (!machinesByVersion[version]) {
        machinesByVersion[version] = [];
      }

      machinesByVersion[version].push(machineId);
    });

    return machinesByVersion;
  } catch (error) {
    console.error('Error fetching machine versions:', error);
    return {};
  }
};

// Helper to get machines for a specific version
export const getMachinesForVersion = (
  machinesByVersion: MachinesByVersion,
  versionString: string
): string[] => {
  // Try exact match first
  if (machinesByVersion[versionString]) {
    return machinesByVersion[versionString];
  }

  // Try without 'v' prefix if present
  const withoutV = versionString.replace(/^v/, '');
  if (machinesByVersion[withoutV]) {
    return machinesByVersion[withoutV];
  }

  // Try with 'v' prefix if not present
  const withV = versionString.startsWith('v') ? versionString : `v${versionString}`;
  if (machinesByVersion[withV]) {
    return machinesByVersion[withV];
  }

  return [];
};

// Fetch version file from S3 bucket for a specific machine via Lambda API
export const fetchMachineVersionFromS3 = async (machineId: string): Promise<string | null> => {
  try {
    const url = `${MACHINE_VERSION_API_URL}?machineId=${machineId}`;
    console.log(`Fetching version from S3 via API: ${url}`);

    const response = await axios.get(url);

    const version = response.data.version;
    console.log(`Machine ${machineId} version from S3: ${version}`);
    return version;
  } catch (error) {
    console.error(`Error fetching version from S3 for ${machineId}:`, error);
    return null;
  }
};
